import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { FaCheck, FaTimes, FaEye } from 'react-icons/fa';
import AdminLayout from '../../components/AdminLayout';

export default function ProductApprovals() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [productToReject, setProductToReject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPendingProducts();
  }, [API]);

  const fetchPendingProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products/status/pending`);
      setPendingProducts(response.data);
    } catch (error) {
      console.error('Error fetching pending products:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors du chargement' : 'Error loading products');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (productId) => {
    try {
      await axios.post(`${API}/products/${productId}/approve`);
      toast.success(i18n.language === 'fr' ? 'Produit approuvé avec succès' : 'Product approved successfully');
      fetchPendingProducts();
    } catch (error) {
      console.error('Error approving product:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors de l\'approbation' : 'Error approving product');
    }
  };

  const handleReject = async () => {
    if (!productToReject) return;
    
    try {
      await axios.post(`${API}/products/${productToReject.id}/reject`, null, {
        params: { reason: rejectReason }
      });
      toast.success(i18n.language === 'fr' ? 'Produit rejeté' : 'Product rejected');
      setRejectDialogOpen(false);
      setRejectReason('');
      setProductToReject(null);
      fetchPendingProducts();
    } catch (error) {
      console.error('Error rejecting product:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors du rejet' : 'Error rejecting product');
    }
  };

  const openRejectDialog = (product) => {
    setProductToReject(product);
    setRejectDialogOpen(true);
  };

  const viewProduct = (product) => {
    setSelectedProduct(product);
    setDialogOpen(true);
  };

  const getProductName = (product) => {
    return i18n.language === 'fr' ? product.name_fr : product.name_en;
  };

  const getProductDescription = (product) => {
    return i18n.language === 'fr' ? product.description_fr : product.description_en;
  };

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0A2540] mb-2">
            {i18n.language === 'fr' ? 'Approbation des Produits' : 'Product Approvals'}
          </h1>
          <p className="text-gray-600">
            {i18n.language === 'fr' 
              ? 'Gérer les produits soumis par les fournisseurs' 
              : 'Manage products submitted by suppliers'}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {i18n.language === 'fr' ? 'Chargement...' : 'Loading...'}
            </p>
          </div>
        ) : pendingProducts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              {i18n.language === 'fr' 
                ? 'Aucun produit en attente d\'approbation' 
                : 'No products pending approval'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pendingProducts.map((product, index) => (
              <Card key={product.id} data-testid={`product-${index}`}>
                <CardContent className="p-6">
                  {product.images && product.images.length > 0 && (
                    <img 
                      src={product.images[0]} 
                      alt={getProductName(product)} 
                      className="w-full h-48 object-cover rounded-md mb-4"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                  <div className="mb-3">
                    <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                      {i18n.language === 'fr' ? 'En attente' : 'Pending'}
                    </Badge>
                  </div>
                  <h3 className="font-bold text-xl mb-2 text-[#0A2540]">
                    {getProductName(product)}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {getProductDescription(product)}
                  </p>
                  
                  {/* Product Details */}
                  <div className="mb-4 space-y-1 text-sm">
                    {product.price_new && (
                      <p className="text-gray-700">
                        <strong>{i18n.language === 'fr' ? 'Prix neuf:' : 'New price:'}</strong> ${product.price_new}
                      </p>
                    )}
                    {product.location && (
                      <p className="text-gray-700">
                        <strong>{i18n.language === 'fr' ? 'Localisation:' : 'Location:'}</strong> {product.location}
                      </p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => viewProduct(product)}
                      className="flex-1"
                      data-testid={`view-product-${index}`}
                    >
                      <FaEye className="mr-1" /> {i18n.language === 'fr' ? 'Voir' : 'View'}
                    </Button>
                    <Button 
                      variant="default" 
                      size="sm" 
                      onClick={() => handleApprove(product.id)}
                      className="flex-1 bg-[#635BFF] hover:bg-[#5248d9]"
                      data-testid={`approve-product-${index}`}
                    >
                      <FaCheck className="mr-1" /> {i18n.language === 'fr' ? 'Approuver' : 'Approve'}
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      onClick={() => openRejectDialog(product)}
                      className="flex-1"
                      data-testid={`reject-product-${index}`}
                    >
                      <FaTimes className="mr-1" /> {i18n.language === 'fr' ? 'Rejeter' : 'Reject'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Product Details Modal */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedProduct && getProductName(selectedProduct)}</DialogTitle>
            </DialogHeader>
            {selectedProduct && (
              <div className="space-y-4">
                {selectedProduct.images && selectedProduct.images.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {selectedProduct.images.map((img, idx) => (
                      <img 
                        key={idx}
                        src={img} 
                        alt={`Product ${idx + 1}`} 
                        className="w-full h-48 object-cover rounded-md"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    ))}
                  </div>
                )}
                
                <div>
                  <h4 className="font-semibold mb-2">
                    {i18n.language === 'fr' ? 'Description:' : 'Description:'}
                  </h4>
                  <p className="text-gray-700">{getProductDescription(selectedProduct)}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {selectedProduct.price_new && (
                    <div>
                      <h4 className="font-semibold">{i18n.language === 'fr' ? 'Prix Neuf:' : 'New Price:'}</h4>
                      <p>${selectedProduct.price_new}</p>
                    </div>
                  )}
                  {selectedProduct.price_used && (
                    <div>
                      <h4 className="font-semibold">{i18n.language === 'fr' ? 'Prix Occasion:' : 'Used Price:'}</h4>
                      <p>${selectedProduct.price_used}</p>
                    </div>
                  )}
                  {selectedProduct.price_rental && (
                    <div>
                      <h4 className="font-semibold">{i18n.language === 'fr' ? 'Prix Location:' : 'Rental Price:'}</h4>
                      <p>${selectedProduct.price_rental}/jour</p>
                    </div>
                  )}
                  {selectedProduct.location && (
                    <div>
                      <h4 className="font-semibold">{i18n.language === 'fr' ? 'Localisation:' : 'Location:'}</h4>
                      <p>{selectedProduct.location}</p>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-4">
                  <Button 
                    variant="default" 
                    onClick={() => {
                      handleApprove(selectedProduct.id);
                      setDialogOpen(false);
                    }}
                    className="flex-1 bg-[#635BFF] hover:bg-[#5248d9]"
                  >
                    <FaCheck className="mr-2" /> {i18n.language === 'fr' ? 'Approuver' : 'Approve'}
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={() => {
                      setDialogOpen(false);
                      openRejectDialog(selectedProduct);
                    }}
                    className="flex-1"
                  >
                    <FaTimes className="mr-2" /> {i18n.language === 'fr' ? 'Rejeter' : 'Reject'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {i18n.language === 'fr' ? 'Rejeter le Produit' : 'Reject Product'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-600">
                {i18n.language === 'fr' 
                  ? 'Veuillez fournir une raison pour le rejet (optionnel):' 
                  : 'Please provide a reason for rejection (optional):'}
              </p>
              <Textarea 
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={i18n.language === 'fr' ? 'Raison du rejet...' : 'Reason for rejection...'}
                rows={4}
              />
              <div className="flex gap-2 justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setRejectDialogOpen(false);
                    setRejectReason('');
                    setProductToReject(null);
                  }}
                >
                  {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleReject}
                >
                  {i18n.language === 'fr' ? 'Confirmer le Rejet' : 'Confirm Rejection'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
